import { useState } from "react";
import Chip from "../../components/Chip";
import EntityPicker from "../../components/EntityPicker";
import FormField from "../../components/FormField";
import TextArea from "../../components/TextArea";
import TextInput from "../../components/TextInput";
import { useLieuxList } from "../../../hooks/useLieuxList";
import type { ArtistDto, LieuMiniInArtistResponse } from "../../../api/artistsApi";

interface Props {
  value: ArtistDto;
  onChange: (next: ArtistDto) => void;
  /** Currently attached lieux, used to render removable chips. Passed by the edit page from the fresh response. */
  attachedLieux?: LieuMiniInArtistResponse[];
}

export default function ArtistFormFields({ value, onChange, attachedLieux }: Props) {
  const [lieuSearch, setLieuSearch] = useState("");
  const lieuxQ = useLieuxList({ search: lieuSearch, page: 0, size: 30, sort: "name,asc" });

  const set = <K extends keyof ArtistDto>(key: K, v: ArtistDto[K]) => onChange({ ...value, [key]: v });
  const selectedIds = value.lieuIds ?? [];

  const addLieu = (lieuId: string) => {
    if (selectedIds.includes(lieuId)) return;
    set("lieuIds", [...selectedIds, lieuId]);
  };
  const removeLieu = (lieuId: string) => {
    set("lieuIds", selectedIds.filter((id) => id !== lieuId));
  };

  // Chips: prefer the attachedLieux for name lookup (they come from the fresh response),
  // fall back to the picker options for lieux just added in this edit session.
  const chipData: LieuMiniInArtistResponse[] = selectedIds.map((id) => {
    const fromAttached = (attachedLieux ?? []).find((l) => l.id === id);
    if (fromAttached) return fromAttached;
    const fromCatalog = lieuxQ.data?.content.find((l) => l.id === id);
    return { id, name: fromCatalog?.name ?? id, addressName: fromCatalog?.addressName };
  });

  const pickerOptions =
    lieuxQ.data?.content.map((l) => ({
      id: l.id,
      label: l.name,
      sublabel: l.addressName || [l.address?.street, l.address?.city].filter(Boolean).join(" · "),
    })) ?? [];

  return (
    <div className="bo-stack">
      <FormField label="Nom" required>
        <TextInput value={value.name ?? ""} onChange={(e) => set("name", e.target.value)} required />
      </FormField>

      <FormField label="Description">
        <TextArea
          value={value.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
          rows={5}
          placeholder="Discipline, biographie, démarche…"
        />
      </FormField>

      <FormField label="Lien vers la photo" hint="URL absolue (https://…)">
        <TextInput
          type="url"
          value={value.photoLink ?? ""}
          onChange={(e) => set("photoLink", e.target.value)}
          placeholder="https://…"
        />
      </FormField>

      <fieldset className="bo-fieldset">
        <legend>Lieux d'exposition</legend>
        {chipData.length === 0 ? (
          <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
            Aucun lieu associé pour le moment.
          </p>
        ) : (
          <div className="bo-chip-list">
            {chipData.map((l) => (
              <Chip
                key={l.id}
                label={l.name + (l.addressName ? ` · ${l.addressName}` : "")}
                onRemove={() => removeLieu(l.id)}
              />
            ))}
          </div>
        )}
        <div style={{ marginTop: "var(--sp-3)" }}>
          <EntityPicker
            placeholder="Rechercher un lieu…"
            options={pickerOptions}
            excludeIds={selectedIds}
            onSearchChange={setLieuSearch}
            onPick={(o) => addLieu(o.id)}
            loading={lieuxQ.isLoading}
          />
        </div>
      </fieldset>
    </div>
  );
}
